import {
  type AlertAuthStatus,
  type AlertHistoryResponse,
  type AlertOverviewResponse,
  type AlertPushActionResponse,
  type AlertPushConfigResponse,
  type AlertPushRegistrationResponse,
  type AlertPushSubscriptionInput,
  type AlertRuleInput,
  type AlertWatchlistInput,
} from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const ALERT_QUERY_KEY = ["alerts"] as const;
const ALERT_PUSH_QUERY_KEY = [...ALERT_QUERY_KEY, "push"] as const;
const ALERT_HISTORY_QUERY_KEY = [...ALERT_QUERY_KEY, "history"] as const;

interface AlertErrorBody {
  error?: string;
  detail?: string;
  code?: string;
}

export class AlertRequestError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
  }
}

async function readErrorBody(res: Response): Promise<AlertErrorBody | null> {
  return await res.json().catch(() => null) as AlertErrorBody | null;
}

async function throwAlertError(res: Response): Promise<never> {
  const body = await readErrorBody(res);
  throw new AlertRequestError(body?.detail || body?.error || res.statusText || "Request failed", body?.code);
}

async function alertRequest<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      "X-Requested-With": "XMLHttpRequest",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) await throwAlertError(res);
  return await res.json() as T;
}

async function fetchAlertAuth(): Promise<AlertAuthStatus> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  if (!res.ok) await throwAlertError(res);
  return await res.json() as AlertAuthStatus;
}

async function fetchAlertOverview(): Promise<AlertOverviewResponse> {
  return alertRequest<AlertOverviewResponse>("GET", "/api/alerts/overview");
}

async function fetchAlertHistory(): Promise<AlertHistoryResponse> {
  return alertRequest<AlertHistoryResponse>("GET", "/api/alerts/history?limit=20");
}

function useInvalidateAlerts() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ALERT_QUERY_KEY });
}

export function useAlertAuth() {
  return useQuery<AlertAuthStatus>({
    queryKey: [...ALERT_QUERY_KEY, "auth"],
    queryFn: fetchAlertAuth,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useAlertOverview(enabled: boolean) {
  return useQuery<AlertOverviewResponse>({
    queryKey: [...ALERT_QUERY_KEY, "overview"],
    queryFn: fetchAlertOverview,
    enabled,
    // Match the main Signals cadence so a signed-in alert page does not create
    // a second, higher-frequency upstream fetch path. Users can still request
    // an immediate evaluation with the visible Refresh matches control.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useAlertHistory(enabled: boolean) {
  return useQuery<AlertHistoryResponse>({
    queryKey: ALERT_HISTORY_QUERY_KEY,
    queryFn: fetchAlertHistory,
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useAlertPushConfig(enabled: boolean) {
  return useQuery<AlertPushConfigResponse>({
    queryKey: [...ALERT_PUSH_QUERY_KEY, "config"],
    queryFn: () => alertRequest<AlertPushConfigResponse>("GET", "/api/alerts/push/config"),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

function useInvalidateAlertPush() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ALERT_PUSH_QUERY_KEY });
}

export function useRegisterAlertPushSubscription() {
  const invalidate = useInvalidateAlertPush();
  return useMutation({
    mutationFn: ({ subscription, confirmTransfer }: { subscription: AlertPushSubscriptionInput; confirmTransfer: boolean }) =>
      alertRequest<AlertPushRegistrationResponse>("POST", "/api/alerts/push/subscriptions", { subscription, confirmTransfer }),
    onSuccess: invalidate,
  });
}

export function useTestAlertPushSubscription() {
  const invalidate = useInvalidateAlertPush();
  return useMutation({
    mutationFn: (subscription: AlertPushSubscriptionInput) =>
      alertRequest<AlertPushActionResponse>("POST", "/api/alerts/push/test", { subscription }),
    onSuccess: invalidate,
  });
}

export function useRevokeAlertPushSubscription() {
  const invalidate = useInvalidateAlertPush();
  return useMutation({
    mutationFn: (subscription: AlertPushSubscriptionInput) =>
      alertRequest<AlertPushActionResponse>("DELETE", "/api/alerts/push/subscriptions", { subscription }),
    onSuccess: invalidate,
  });
}

export function useCreateAlertWatchlist() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: (input: AlertWatchlistInput) => alertRequest("POST", "/api/alerts/watchlists", input),
    onSuccess: invalidate,
  });
}

export function useUpdateAlertWatchlist() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AlertWatchlistInput }) =>
      alertRequest("PATCH", `/api/alerts/watchlists/${encodeURIComponent(id)}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteAlertWatchlist() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: (id: string) => alertRequest("DELETE", `/api/alerts/watchlists/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

export function useCreateAlertRule() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: (input: AlertRuleInput) => alertRequest("POST", "/api/alerts/rules", input),
    onSuccess: invalidate,
  });
}

export function useUpdateAlertRule() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AlertRuleInput }) =>
      alertRequest("PATCH", `/api/alerts/rules/${encodeURIComponent(id)}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteAlertRule() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: (id: string) => alertRequest("DELETE", `/api/alerts/rules/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}
